/**
 * The generated interface of a chat line (ChatMessage.ui, show_ui), drawn with json-render (@json-render/react) in the
 * agent's message, under its text. The registry gives each component of the shared catalog (src/shared/chat-ui.ts)
 * the chat's compact look (cards with a 14 px radius, 34 px pills, the app's tokens), and the actions their behaviour:
 * `ask` sends the message through the chat's own send path as a reply to the line, `open` goes to a page of the app
 * (the catalog's closed list), `url` opens an https address in a new tab (noopener, noreferrer). An Option whose
 * message was sent as a reply to the line (pending or delivered, from any device) gets a check and its siblings dim (a
 * Product of a Products group, and a Journey among the Journeys of its parent, likewise), and never sends it again. A
 * Product or Journey is a link with a url press, a button with an ask or open press, plain otherwise. A Product shows
 * the photo show_ui stored (GET /api/files/*), never an outside address.
 * Elements appear one after the other (fade and a 6 px rise, 40 ms apart), without motion when the reader asks for less.
 */
import { useNavigate } from "@tanstack/react-router";
import { defineRegistry, JSONUIProvider, Renderer } from "@json-render/react";
import type { Spec } from "@json-render/core";
import { Children, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { chatUiCatalog, isHttpsUrl, JOURNEY_ICONS } from "../../shared/chat-ui";
import { CHAT_UI_ROUTES, type ChatUiComponent, type ChatUiRoute, type ChatUiSpec } from "../../shared/contracts";
import { fileUrl } from "../lib/api";
import { IconCheck, IconChevronRight, IconExternal } from "./icons";
import { nb, SKY, SkyIcon } from "./pulse-summary";
import { cx } from "./ui";

/** What the view adds to an element's props (never sent by the server): its place in the stagger, its state. */
interface Extra {
  /** Order of appearance (40 ms apart). */
  __i?: number;
  /** An answered Option ("chosen") and its siblings ("dim"). */
  __state?: "chosen" | "dim";
  /** The address of a `url` press: drawn as a real link. */
  __href?: string;
  /** A Product's stored photo (its app URL). */
  __photo?: string;
  /** A press that sends a message or opens a page of the app (a Product or Journey is a button only then). */
  __press?: boolean;
}

function extra(props: unknown): Extra {
  return props as Extra;
}

/** The fade-and-rise of an element, `i` steps of 40 ms after the first. */
function rise(i: number | undefined): CSSProperties {
  return { "--ui-i": i ?? 0 } as CSSProperties;
}

function hasKids(children: ReactNode): boolean {
  return Array.isArray(children) ? children.length > 0 : Boolean(children);
}

type Tone = "neutral" | "accent" | "warning" | "success" | null | undefined;

const CARD_TONE: Record<string, string> = {
  neutral: "bg-inset",
  accent: "bg-inset shadow-[inset_3px_0_0_var(--accent)]",
  warning: "bg-inset shadow-[inset_3px_0_0_var(--warning)]",
  success: "bg-inset shadow-[inset_3px_0_0_var(--success)]",
};

const VALUE_TONE: Record<string, string> = {
  neutral: "text-fg",
  accent: "text-accent",
  warning: "text-warning",
  success: "text-success",
};

const decimal = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });

/** Elements that rise into view (layout containers do not: their content does). */
const RISING: ReadonlySet<ChatUiComponent> = new Set<ChatUiComponent>([
  "Headline",
  "Text",
  "DayTimeline",
  "TimelineItem",
  "Highlight",
  "Stat",
  "PersonChip",
  "Weather",
  "Progress",
  "Option",
  "LinkCard",
  "Sparkline",
  "Checklist",
  "CheckItem",
  "ActionChip",
  "Table",
  "Product",
  "Journey",
]);

/**
 * The spec as drawn: each rising element gets its order of appearance (tree order), each url press its address, and
 * the Options of a group their state from the answers to the line (the newest answer that is one of its messages).
 */
function viewSpec(spec: ChatUiSpec, answers: readonly string[]): Spec {
  const order = new Map<string, number>();
  const walk = (id: string) => {
    const e = Object.hasOwn(spec.elements, id) ? spec.elements[id] : undefined;
    if (!e || order.has(id)) return;
    if (RISING.has(e.type)) order.set(id, order.size);
    for (const c of e.children) walk(c);
  };
  walk(spec.root);
  const state = new Map<string, "chosen" | "dim">();
  const askOf = (id: string): string | null => {
    const press = spec.elements[id]?.on?.press;
    return press?.action === "ask" ? press.params.message.trim() : null;
  };
  for (const e of Object.values(spec.elements)) {
    // The choices of a group: an Options' or a Products' children, the Journeys that answer a message among a parent's.
    const group = e.type === "Options" || e.type === "Products" ? e.children : e.children.filter((c) => spec.elements[c]?.type === "Journey" && askOf(c) !== null);
    if (!group.length) continue;
    const messages = group.map(askOf);
    const picked = [...answers].reverse().find((a) => messages.includes(a));
    if (picked === undefined) continue;
    group.forEach((c, n) => state.set(c, messages[n] === picked ? "chosen" : "dim"));
  }
  const elements = Object.fromEntries(
    Object.entries(spec.elements).map(([id, e]) => {
      const press = e.on?.press;
      const added: Extra = {
        ...(order.has(id) ? { __i: order.get(id) } : {}),
        ...(state.has(id) ? { __state: state.get(id) } : {}),
        ...(press?.action === "url" ? { __href: press.params.href } : {}),
        ...(press?.action === "ask" || press?.action === "open" ? { __press: true } : {}),
        ...(e.photo ? { __photo: fileUrl(e.photo) } : {}),
      };
      return [id, { ...e, props: { ...e.props, ...added } }];
    }),
  );
  return { root: spec.root, elements } as unknown as Spec;
}

/** A 34 px pill on a chip (44 px tall on a phone). */
const PILL =
  "chat-ui-rise relative inline-flex h-11 max-w-full items-center gap-1.5 rounded-full bg-chip px-3.5 text-[14px] font-medium text-fg transition hover:bg-line-strong md:h-[34px] md:after:absolute md:after:inset-x-0 md:after:-inset-y-[5px] md:after:content-['']";
/** A card inside the agent's bubble: the inset fill (rgba 255 · 0.07), 14 px corners. */
const CARD = "chat-ui-rise relative flex w-full min-w-0 items-start gap-2.5 rounded-[14px] bg-inset px-3 py-2.5 text-left";

/**
 * The registry, built once: the actions run through the handlers each view gives its JSONUIProvider (these are never
 * called; defineRegistry wants them for the catalog's actions).
 */
const { registry } = defineRegistry(chatUiCatalog, {
  components: {
    Stack: ({ props, children }) => <div className={cx("flex min-w-0 flex-col", props.gap === "md" ? "gap-3" : "gap-2")}>{children}</div>,
    Row: ({ children }) => <div className="flex min-w-0 flex-wrap gap-2">{children}</div>,
    Headline: ({ props }) => (
      <p className="chat-ui-rise text-[17px] leading-snug font-semibold wrap-anywhere" style={rise(extra(props).__i)}>
        {nb(props.text)}
      </p>
    ),
    Text: ({ props }) => (
      <p className="chat-ui-rise text-[15px] leading-normal text-fg wrap-anywhere" style={rise(extra(props).__i)}>
        {nb(props.text)}
      </p>
    ),
    DayTimeline: ({ props, children }) => (
      <div className="min-w-0">
        {props.title ? (
          <p className="chat-ui-rise mb-1 font-display text-[18px] leading-tight" style={rise(extra(props).__i)}>
            {props.title}
          </p>
        ) : null}
        <ol aria-label={props.title ?? undefined} className="flex min-w-0 flex-col">
          {children}
        </ol>
      </div>
    ),
    TimelineItem: ({ props }) => (
      <li className="chat-ui-rise grid min-w-0 grid-cols-[3.25rem_minmax(0,1fr)] gap-x-2 py-1.5" style={rise(extra(props).__i)}>
        <span className={cx("pt-px text-[13.5px] whitespace-nowrap tabular-nums", props.tone === "accent" || props.tone === "warning" ? "font-semibold text-accent" : "text-muted")}>
          {nb(props.time)}
        </span>
        <span className="min-w-0">
          <span className="block text-[15px] leading-snug wrap-anywhere">{nb(props.label)}</span>
          {props.note ? <span className="block text-[13px] leading-snug text-subtle wrap-anywhere">{nb(props.note)}</span> : null}
        </span>
      </li>
    ),
    Highlight: ({ props, children }) => (
      <div className={cx("chat-ui-rise min-w-0 rounded-[14px] px-3.5 py-3", CARD_TONE[(props.tone as Tone) ?? "neutral"])} style={rise(extra(props).__i)}>
        <p className="text-[15px] leading-snug font-semibold wrap-anywhere">{nb(props.title)}</p>
        {props.text ? <p className="mt-0.5 text-[14px] leading-snug text-muted wrap-anywhere">{nb(props.text)}</p> : null}
        {hasKids(children) ? <div className="mt-2.5 flex flex-wrap gap-2">{children}</div> : null}
      </div>
    ),
    Stat: ({ props }) => (
      <div className="chat-ui-rise flex min-w-[84px] flex-col rounded-[14px] bg-inset px-3.5 py-2.5" style={rise(extra(props).__i)}>
        <span className={cx("font-display text-[26px] leading-none tracking-[-0.01em] whitespace-nowrap tabular-nums", VALUE_TONE[(props.tone as Tone) ?? "neutral"])}>
          {nb(props.value)}
        </span>
        <span className="mt-1 text-[12.5px] leading-snug text-muted">{props.label}</span>
      </div>
    ),
    PersonChip: ({ props }) => (
      <div className="chat-ui-rise flex max-w-full min-w-0 items-center gap-2 rounded-full bg-inset py-1 pr-3.5 pl-1" style={rise(extra(props).__i)}>
        <span aria-hidden="true" className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-chip text-[13px] font-semibold">
          {props.name.trim().charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 truncate text-[14px]">
          <span className="font-semibold">{props.name}</span> <span className="text-muted">{props.why}</span>
        </span>
      </div>
    ),
    Weather: ({ props }) => {
      const sky = SKY[props.sky] ?? SKY.mixed!;
      const when = [props.day, props.place].filter(Boolean).join(" · ");
      return (
        <div data-testid="chat-ui-weather" className="chat-ui-rise flex min-w-0 items-center gap-2.5 rounded-[14px] bg-inset px-3.5 py-2.5" style={rise(extra(props).__i)}>
          <span role="img" aria-label={sky.label} className="shrink-0">
            <SkyIcon sky={props.sky} className="h-6 w-6 text-muted" />
          </span>
          <span className="min-w-0">
            {when ? <span className="block text-[12.5px] leading-snug text-muted wrap-anywhere">{when}</span> : null}
            <span className="block text-[20px] leading-none font-semibold tabular-nums">{props.temp}</span>
          </span>
          {props.hint ? <span className="min-w-0 text-[13.5px] leading-snug text-muted wrap-anywhere">{props.hint}</span> : null}
        </div>
      );
    },
    Progress: ({ props }) => (
      <div className="chat-ui-rise min-w-0" style={rise(extra(props).__i)}>
        <div className="flex items-baseline justify-between gap-3 text-[14px]">
          <span className="min-w-0 wrap-anywhere">{props.label}</span>
          <span className="shrink-0 text-muted tabular-nums">{props.percent}&nbsp;%</span>
        </div>
        <div
          className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-chip"
          role="progressbar"
          aria-valuenow={props.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={props.label}
        >
          <div className="h-full rounded-full bg-accent" style={{ width: `${props.percent}%` }} />
        </div>
      </div>
    ),
    Options: ({ props, children }) => (
      <div
        role="group"
        aria-label="Choix"
        className={cx("min-w-0", props.layout === "grid" ? cx("grid grid-cols-2 gap-2", Children.count(children) === 3 && "md:grid-cols-3") : "flex flex-col gap-2")}
      >
        {children}
      </div>
    ),
    Option: ({ props, emit }) => {
      const { __i, __state, __href } = extra(props);
      const chosen = __state === "chosen";
      const body = (
        <>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] leading-snug font-semibold wrap-anywhere">{nb(props.title)}</span>
            {props.subtitle ? <span className="mt-0.5 block text-[13.5px] leading-snug text-muted wrap-anywhere">{nb(props.subtitle)}</span> : null}
            {props.meta ? <span className="mt-0.5 block text-[12.5px] leading-snug text-subtle wrap-anywhere">{nb(props.meta)}</span> : null}
            {props.badge ? (
              <span className="mt-2 inline-flex max-w-full rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-accent-fg">
                <span className="truncate">{props.badge}</span>
              </span>
            ) : null}
          </span>
          {chosen ? (
            <span data-testid="chat-ui-check" className="cv-pop absolute top-2 right-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ink text-ink-fg">
              <IconCheck width={11} height={11} strokeWidth={3.4} />
              <span className="sr-only">Choisi</span>
            </span>
          ) : __href ? (
            <IconExternal width={16} height={16} className="mt-0.5 shrink-0 text-muted" />
          ) : null}
        </>
      );
      const cls = cx(
        CARD,
        "transition-[opacity,box-shadow,background-color] duration-200",
        chosen ? "pr-8 shadow-[inset_0_0_0_1.5px_var(--fg)]" : "hover:bg-[rgb(255_255_255/0.1)]",
        __state === "dim" && "opacity-40",
      );
      if (__href) {
        return (
          <a data-testid="chat-ui-option" href={__href} target="_blank" rel="noopener noreferrer" className={cls} style={rise(__i)}>
            {body}
          </a>
        );
      }
      return (
        <button
          type="button"
          data-testid="chat-ui-option"
          data-state={__state}
          aria-pressed={__state ? chosen : undefined}
          onClick={() => {
            if (!chosen) emit("press");
          }}
          className={cls}
          style={rise(__i)}
        >
          {body}
        </button>
      );
    },
    LinkCard: ({ props, emit }) => {
      const { __i, __href } = extra(props);
      const body = (
        <>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-semibold">{props.title}</span>
            <span className="block truncate text-[12.5px] text-subtle">{props.site}</span>
          </span>
          {__href ? <IconExternal width={18} height={18} className="shrink-0 text-muted" /> : <IconChevronRight width={18} height={18} className="shrink-0 text-muted" />}
        </>
      );
      const cls = cx(CARD, "items-center transition hover:bg-[rgb(255_255_255/0.1)]");
      return __href ? (
        <a data-testid="chat-ui-link" href={__href} target="_blank" rel="noopener noreferrer" className={cls} style={rise(__i)}>
          {body}
        </a>
      ) : (
        <button type="button" data-testid="chat-ui-link" onClick={() => emit("press")} className={cls} style={rise(__i)}>
          {body}
        </button>
      );
    },
    Sparkline: ({ props }) => {
      const values = props.values;
      const min = Math.min(...values);
      const span = Math.max(...values) - min || 1;
      const points = values.map((v, n) => `${((n / (values.length - 1)) * 100).toFixed(2)},${(36 - ((v - min) / span) * 32).toFixed(2)}`).join(" ");
      const last = values[values.length - 1]!;
      return (
        <div className="chat-ui-rise min-w-0" style={rise(extra(props).__i)}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 text-[13.5px] text-muted wrap-anywhere">{props.label}</span>
            <span className="shrink-0 text-[17px] font-semibold tabular-nums">
              {decimal.format(last)}
              {props.unit ? <span className="text-[13px] font-normal text-muted">&nbsp;{props.unit}</span> : null}
            </span>
          </div>
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="mt-1 h-10 w-full text-accent" role="img" aria-label={`${props.label} : ${values.map((v) => decimal.format(v)).join(", ")}`}>
            <polyline points={points} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      );
    },
    Checklist: ({ props, children }) => (
      <div className="min-w-0">
        {props.title ? (
          <p className="chat-ui-rise mb-1 text-[14px] font-semibold text-muted" style={rise(extra(props).__i)}>
            {props.title}
          </p>
        ) : null}
        <ul aria-label={props.title ?? undefined} className="flex min-w-0 flex-col">
          {children}
        </ul>
      </div>
    ),
    CheckItem: ({ props }) => (
      <li className="chat-ui-rise flex min-w-0 items-start gap-2.5 py-1" style={rise(extra(props).__i)}>
        <span
          aria-hidden="true"
          className={cx("mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full", props.done ? "bg-fg text-bg" : "border border-line-strong")}
        >
          {props.done ? <IconCheck width={11} height={11} strokeWidth={2.8} /> : null}
        </span>
        <span className={cx("min-w-0 text-[15px] leading-snug wrap-anywhere", props.done && "text-muted line-through")}>
          <span className="sr-only">{props.done ? "Fait : " : "À faire : "}</span>
          {nb(props.label)}
        </span>
      </li>
    ),
    ActionChip: ({ props, emit }) => {
      const { __i, __href } = extra(props);
      return __href ? (
        <a href={__href} target="_blank" rel="noopener noreferrer" className={PILL} style={rise(__i)}>
          <span className="truncate">{props.label}</span>
          <IconExternal width={14} height={14} className="shrink-0 text-muted" />
        </a>
      ) : (
        <button type="button" onClick={() => emit("press")} className={PILL} style={rise(__i)}>
          <span className="truncate">{props.label}</span>
        </button>
      );
    },
    Table: ({ props }) => (
      <div data-testid="chat-ui-table" className="chat-ui-rise min-w-0 overflow-x-auto rounded-[14px] border border-line bg-elev" style={rise(extra(props).__i)}>
        {props.title ? <p className="px-3.5 pt-3 text-[14px] font-semibold">{nb(props.title)}</p> : null}
        <table className="w-full border-collapse text-left text-[14px]">
          <thead>
            <tr>
              {props.columns.map((c, n) => (
                <th key={n} scope="col" className="px-3.5 pt-2.5 pb-1.5 text-[12px] font-medium text-muted">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row, r) => (
              <tr key={r} data-highlight={props.highlight === r + 1 ? "true" : undefined} className={cx("border-t border-line", props.highlight === r + 1 && "bg-accent-soft")}>
                {row.map((cell, n) =>
                  n === 0 ? (
                    <th key={n} scope="row" className="px-3.5 py-2 font-semibold wrap-anywhere">
                      {nb(cell)}
                    </th>
                  ) : (
                    <td key={n} className="px-3.5 py-2 tabular-nums wrap-anywhere">
                      {nb(cell)}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
    Products: ({ props, children }) => (
      <div role="group" aria-label="Produits" className={cx("min-w-0", props.layout === "list" ? "flex flex-col gap-2" : "grid grid-cols-2 gap-2")}>
        {children}
      </div>
    ),
    Product: ({ props, emit }) => {
      const { __i, __state, __href, __photo, __press } = extra(props);
      const chosen = __state === "chosen";
      const body = (
        <>
          {__photo ? (
            <img src={__photo} alt="" loading="lazy" className="aspect-square w-full rounded-[10px] bg-sunken object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
          ) : null}
          <span className="block min-w-0">
            <span className="block text-[14.5px] leading-snug font-semibold wrap-anywhere">{nb(props.title)}</span>
            {props.subtitle ? <span className="mt-0.5 block text-[12.5px] leading-snug text-muted wrap-anywhere">{nb(props.subtitle)}</span> : null}
            <span className="mt-1 flex flex-wrap items-center gap-1.5">
              {props.price ? <span className="text-[15px] font-semibold tabular-nums">{nb(props.price)}</span> : null}
              {props.badge ? <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold text-accent-ink">{props.badge}</span> : null}
              {chosen ? (
                <span data-testid="chat-ui-check" className="ml-auto flex h-[20px] w-[20px] items-center justify-center rounded-full bg-accent text-accent-fg">
                  <IconCheck width={12} height={12} strokeWidth={2.6} />
                  <span className="sr-only">Choisi</span>
                </span>
              ) : __href ? (
                <IconExternal width={14} height={14} className="ml-auto text-muted" />
              ) : null}
            </span>
          </span>
        </>
      );
      const cls = cx(
        "chat-ui-rise flex w-full min-w-0 flex-col gap-2 rounded-[14px] border p-2.5 text-left transition-[opacity,background-color,border-color] duration-200",
        chosen ? "border-accent/50 bg-accent-soft" : "border-line bg-elev",
        (__href || __press) && !chosen && "hover:bg-sunken",
        __state === "dim" && "opacity-40",
      );
      if (__href) {
        return (
          <a data-testid="chat-ui-product" data-state={__state} href={__href} target="_blank" rel="noopener noreferrer" className={cls} style={rise(__i)}>
            {body}
          </a>
        );
      }
      if (__press) {
        return (
          <button
            type="button"
            data-testid="chat-ui-product"
            data-state={__state}
            aria-pressed={__state ? chosen : undefined}
            onClick={() => {
              if (!chosen) emit("press");
            }}
            className={cls}
            style={rise(__i)}
          >
            {body}
          </button>
        );
      }
      return (
        <div data-testid="chat-ui-product" data-state={__state} className={cls} style={rise(__i)}>
          {body}
        </div>
      );
    },
    Journey: ({ props, emit }) => {
      const { __i, __state, __href, __press } = extra(props);
      const chosen = __state === "chosen";
      const details = [props.duration, props.carrier].filter(Boolean).join(" · ");
      const body = (
        <>
          <span aria-hidden="true" className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-sunken text-[17px]">
            {JOURNEY_ICONS[props.mode]}
          </span>
          <span className="min-w-0 flex-1">
            <span className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-baseline gap-x-2">
              <span className="min-w-0">
                {props.depart ? <span className="block text-[17px] leading-tight font-semibold tabular-nums">{nb(props.depart)}</span> : null}
                <span className="block text-[13px] leading-snug text-muted wrap-anywhere">{props.from}</span>
              </span>
              <span aria-label="vers" className="text-muted">
                →
              </span>
              <span className="min-w-0 text-right">
                {props.arrive ? <span className="block text-[17px] leading-tight font-semibold tabular-nums">{nb(props.arrive)}</span> : null}
                <span className="block text-[13px] leading-snug text-muted wrap-anywhere">{props.to}</span>
              </span>
            </span>
            {details || props.price ? (
              <span className="mt-1.5 flex items-baseline justify-between gap-2 text-[12.5px] text-subtle">
                <span className="min-w-0 wrap-anywhere">{nb(details)}</span>
                {props.price ? <span className="shrink-0 text-[14px] font-semibold text-fg tabular-nums">{nb(props.price)}</span> : null}
              </span>
            ) : null}
            {props.note ? <span className={cx("mt-1 block text-[12.5px] leading-snug wrap-anywhere", props.tone === "warning" ? "text-warning" : "text-muted")}>{nb(props.note)}</span> : null}
          </span>
          {chosen ? (
            <span data-testid="chat-ui-check" className="flex h-[22px] w-[22px] shrink-0 items-center justify-center self-center rounded-full bg-accent text-accent-fg">
              <IconCheck width={13} height={13} strokeWidth={2.6} />
              <span className="sr-only">Choisi</span>
            </span>
          ) : __href ? (
            <IconExternal width={16} height={16} className="mt-0.5 shrink-0 text-muted" />
          ) : null}
        </>
      );
      const cls = cx(
        CARD,
        "transition-[opacity,background-color,border-color] duration-200",
        chosen
          ? "border-accent/50 bg-accent-soft"
          : props.tone === "warning"
            ? "border-warning/40 bg-warning-soft"
            : props.tone === "accent"
              ? "border-accent/40 bg-accent-soft"
              : "border-line bg-elev",
        (__href || __press) && !chosen && "hover:bg-sunken",
        __state === "dim" && "opacity-40",
      );
      if (__href) {
        return (
          <a data-testid="chat-ui-journey" data-state={__state} href={__href} target="_blank" rel="noopener noreferrer" className={cls} style={rise(__i)}>
            {body}
          </a>
        );
      }
      if (__press) {
        return (
          <button
            type="button"
            data-testid="chat-ui-journey"
            data-state={__state}
            aria-pressed={__state ? chosen : undefined}
            onClick={() => {
              if (!chosen) emit("press");
            }}
            className={cls}
            style={rise(__i)}
          >
            {body}
          </button>
        );
      }
      return (
        <div data-testid="chat-ui-journey" data-state={__state} className={cls} style={rise(__i)}>
          {body}
        </div>
      );
    },
  },
  actions: {
    ask: async () => undefined,
    open: async () => undefined,
    url: async () => undefined,
  },
});

/**
 * One line's interface (data-testid="chat-ui"). `answers`: the texts sent as replies to the line, oldest first;
 * `onAsk` sends a message as a reply to it.
 */
export function ChatUiView({ spec, answers, onAsk, className }: { spec: ChatUiSpec; answers: readonly string[]; onAsk: (message: string) => void; className?: string }) {
  const navigate = useNavigate();
  const view = useMemo(() => viewSpec(spec, answers), [spec, answers]);
  // json-render keeps the handlers of its first render: they reach the latest callbacks through refs.
  const latest = useRef({ onAsk, navigate });
  latest.current = { onAsk, navigate };
  const [handlers] = useState(() => ({
    ask: (params: Record<string, unknown>) => {
      const message = typeof params.message === "string" ? params.message.trim() : "";
      if (message) latest.current.onAsk(message);
    },
    open: (params: Record<string, unknown>) => {
      if (typeof params.to === "string" && (CHAT_UI_ROUTES as readonly string[]).includes(params.to)) void latest.current.navigate({ to: params.to as ChatUiRoute });
    },
    url: (params: Record<string, unknown>) => {
      if (typeof params.href === "string" && isHttpsUrl(params.href)) window.open(params.href, "_blank", "noopener,noreferrer");
    },
  }));
  return (
    <div data-testid="chat-ui" className={cx("min-w-0", className)}>
      <JSONUIProvider registry={registry} handlers={handlers}>
        <Renderer spec={view} registry={registry} />
      </JSONUIProvider>
    </div>
  );
}
